// Template types, keyed by the route slug with its dashes removed
// (/templates/purchase-order-template → "purchaseordertemplate"), mapped to
// the TemplateTypeID the template APIs expect.
export const TEMPLATE_TYPE_IDS = {
  invoicetemplate: 1,
  proformainvoicetemplate: 2,
  deliverynotetemplate: 3,
  purchaseordertemplate: 4,
  collectionnotetemplate: 5,
};

// Types whose field definition comes from the generic definition API
// (POST v1/{pos}/template/generic/definition/fetch/typeid) rather than the
// invoice definition API.
const GENERIC_DEFINITION_TYPES = ["purchaseordertemplate"];

export function templateTypeID(templateType) {
  return TEMPLATE_TYPE_IDS[templateType] || 0;
}

export function usesGenericDefinition(templateType) {
  return GENERIC_DEFINITION_TYPES.includes(templateType);
}

// ---- Collection notes ---------------------------------------------------------
// A collection note uses the invoice definitions without anything about
// prices: these fields and definitions are left out of the editor, and
// layouts pasted in lose them (stripPricing).

const PRICING_TYPES = ["collectionnotetemplate"];

export const PRICING_FIELDS = {
  EposSalesDefinition: [
    "SubTotal", "SubTotal_with_VAT", "SubTotal_without_VAT", "NetTotal", "Discount", "VATTotal",
    "PreviousBalance", "Balanceoutstanding", "CurrencyCode", "CurrencySymbol",
  ],
  EposSalesFieldsDefintion: [
    "SalesUnitPrice", "SalesPrice", "RecommendedPrice", "LineAmount", "LineVAT", "LineDiscount", "PromotionAmount",
    "ItemVatCode", "IsPriceNotify", "CurrencyCode", "CurrencySymbol",
  ],
  EposTemplateSettingsDefinition: ["IsPriceChangeNotification", "PriceNotificationColour"],
};

export const PRICING_DEFINITIONS = ["EPOSVatSummaryDefinition", "EPOSCompanyBankDefinition"];

export function hidesPricing(templateType) {
  return PRICING_TYPES.includes(templateType);
}

// Whether a data path ("Definition.Field…") is about prices.
export function isPricingPath(path) {
  const [definition, field] = String(path || "").split(".");
  if (PRICING_DEFINITIONS.includes(definition)) return true;
  // Copies are named "Definition.Field-copy-N"; the field is before the suffix.
  const bare = String(field || "").replace(/-copy-\d+$/, "");
  return !!PRICING_FIELDS[definition]?.includes(bare);
}

// The definition schema (GetSchema's Result) without the pricing parts.
export function withoutPricing(attributes) {
  if (!attributes || typeof attributes !== "object") return attributes;
  const next = {};
  Object.keys(attributes).forEach((definition) => {
    if (PRICING_DEFINITIONS.includes(definition)) return;
    const hidden = PRICING_FIELDS[definition];
    const attribute = attributes[definition];
    if (!hidden || !attribute?.Properties) {
      next[definition] = attribute;
      return;
    }
    const Properties = {};
    Object.keys(attribute.Properties).forEach((field) => {
      if (!hidden.includes(field)) Properties[field] = attribute.Properties[field];
    });
    next[definition] = { ...attribute, Properties };
  });
  return next;
}

// A number format without currency: currency becomes a plain number with the
// same decimals and grouping.
export function withoutCurrency(format) {
  if (!format || format.type !== "currency") return format;
  const { currencyPosition, ...rest } = format;
  return { ...rest, type: "number" };
}

const ITEM_PRICING_COLUMNS = PRICING_FIELDS.EposSalesFieldsDefintion;

// A text element without a currency symbol: neither its format nor the
// older `showCurrency` switch adds one.
function withoutElementCurrency(el) {
  if (!el || (!el.showCurrency && el.format?.type !== "currency")) return el;
  const next = { ...el, format: withoutCurrency(el.format) };
  if (next.showCurrency) next.showCurrency = false;
  if (!next.format) delete next.format;
  return next;
}

// One section's content without pricing: elements bound to pricing fields,
// QR codes bound to them, column totals of price columns and the VAT summary
// are removed; currency formats become plain numbers.
function stripSectionPricing(content) {
  if (!content || typeof content !== "object") return content;
  const next = { ...content };
  if (content.elements) {
    const elements = {};
    Object.keys(content.elements).forEach((key) => {
      const el = content.elements[key];
      if (!el) return;
      if (isPricingPath(el.name)) return;
      if (el.data === "ColumnTotal" && ITEM_PRICING_COLUMNS.includes(el.columnTotal?.column)) return;
      if (/\{VatSummary\}|\[VAT_SUMMARY_TABLE\]/.test(el.text || "") || el.data === "VatSummary") return;
      elements[key] = withoutElementCurrency(el);
    });
    next.elements = elements;
  }
  if (content.qrCodes) {
    const qrCodes = {};
    Object.keys(content.qrCodes).forEach((key) => {
      const qr = content.qrCodes[key];
      if (qr && !(qr.qrMode === "field" && isPricingPath(qr.qrFieldPath))) qrCodes[key] = qr;
    });
    next.qrCodes = qrCodes;
  }
  if (content.VatSummary && Object.keys(content.VatSummary).length > 0) next.VatSummary = {};
  return next;
}

// A whole template (or a copied layout: { prop, head, items, total, foot,
// bandSections }) without pricing. Item columns for price fields go, other
// columns lose currency formats, and price-change highlighting is turned off.
export function stripPricing(template) {
  if (!template || typeof template !== "object") return template;
  const next = { ...template };
  ["head", "total", "foot"].forEach((section) => {
    if (template[section]) next[section] = stripSectionPricing(template[section]);
  });
  (template.bands || []).forEach((band) => {
    if (band?.section && !["head", "items", "total", "foot"].includes(band.section) && template[band.section]) {
      next[band.section] = stripSectionPricing(template[band.section]);
    }
  });
  if (template.bandSections) {
    next.bandSections = {};
    Object.keys(template.bandSections).forEach((key) => {
      next.bandSections[key] = stripSectionPricing(template.bandSections[key]);
    });
  }
  if (template.items?.items) {
    const items = {};
    Object.keys(template.items.items).forEach((key) => {
      const column = template.items.items[key];
      if (!column) return;
      if (ITEM_PRICING_COLUMNS.includes(key)) return;
      if (column.format?.type === "currency" || column.showCurrency) {
        items[key] = { ...column, format: withoutCurrency(column.format || { type: "currency", decimals: 2, thousandsSeparator: true }), showCurrency: false };
      } else {
        items[key] = column;
      }
    });
    const detailRow = template.items.detailRow;
    next.items = { ...template.items, items };
    if (detailRow?.segments) {
      next.items.detailRow = { ...detailRow, segments: detailRow.segments.filter((s) => !ITEM_PRICING_COLUMNS.includes(s.field)) };
    }
  }
  if (Array.isArray(template.bands)) {
    next.bands = template.bands.map((band) => (band?.detailRow?.segments
      ? { ...band, detailRow: { ...band.detailRow, segments: band.detailRow.segments.filter((s) => !ITEM_PRICING_COLUMNS.includes(s.field)) } }
      : band));
  }
  if (template.prop) next.prop = { ...template.prop, isPriceChangeNotification: false };
  return next;
}

// Elements copied from another template, before pasting into one that
// hides pricing (the element clipboard: { items: [{ path, data }] }).
export function stripPricingFromClipboard(payload) {
  if (!payload?.items) return payload;
  const items = payload.items.filter(({ path, data }) => {
    if (!data) return false;
    if (path === "elements") {
      if (isPricingPath(data.name)) return false;
      if (data.data === "ColumnTotal" && ITEM_PRICING_COLUMNS.includes(data.columnTotal?.column)) return false;
    }
    if (path === "qrCodes" && data.qrMode === "field" && isPricingPath(data.qrFieldPath)) return false;
    return true;
  }).map((item) => (item.path === "elements" ? { ...item, data: withoutElementCurrency(item.data) } : item));
  return { ...payload, items };
}
